import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';

const MAX_BYTES = 15 * 1024 * 1024; // mirrors support_service.MAX_BYTES

// Pull image files out of a clipboard paste (Snipping Tool, Cmd+Ctrl+Shift+4,
// "Copy image" in a browser). Returns [] when the paste should be left alone:
// plain text, or pasting into a text box when the clipboard also carries text
// (Excel/Word put a picture AND the text on the clipboard; the text wins there).
export function imagesFromClipboard(e) {
    const cd = e?.clipboardData;
    if (!cd) return [];
    const inEditable = !!e.target?.closest?.('textarea, input, [contenteditable="true"]');
    if (inEditable && (cd.getData('text/plain') || '').trim()) return [];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const out = [];
    Array.from(cd.items || []).forEach((it) => {
        if (it.kind !== 'file' || !(it.type || '').startsWith('image/')) return;
        const f = it.getAsFile();
        if (!f) return;
        const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg').replace(/[^a-z0-9]/gi, '') || 'png';
        const suffix = out.length ? `-${out.length + 1}` : '';
        out.push(new File([f], `screenshot-${stamp}${suffix}.${ext}`, { type: f.type }));
    });
    return out;
}

export function withinSizeLimit(list) {
    const arr = Array.from(list || []);
    const ok = arr.filter(f => f.size <= MAX_BYTES);
    if (ok.length !== arr.length) toast.error('Some files exceed the 15MB limit and were skipped.');
    return ok;
}

const hasFiles = (e) => Array.from(e?.dataTransfer?.types || []).includes('Files');

// Wrap any area so it accepts pasted screenshots and dragged-in files. Paste
// events bubble up from inputs inside it, so wrapping a reply box means Ctrl+V
// straight into the textarea attaches the image. `focusable` lets an area with
// no input of its own (an attachments list) take focus on click so Ctrl+V works
// there too. Handled pastes call preventDefault, which page-level fallback
// listeners check so the same screenshot is never attached twice.
export default function PasteDropZone({ onFiles, children, className = '', disabled = false, focusable = false, testId }) {
    const [over, setOver] = useState(false);
    const depth = useRef(0);

    const accept = (list) => {
        const ok = withinSizeLimit(list);
        if (ok.length) onFiles(ok);
    };

    const onPaste = (e) => {
        if (disabled) return;
        const imgs = imagesFromClipboard(e);
        if (!imgs.length) return;
        e.preventDefault();
        accept(imgs);
    };

    return (
        <div
            tabIndex={focusable && !disabled ? 0 : undefined}
            onPaste={onPaste}
            onDragEnter={e => { if (disabled || !hasFiles(e)) return; e.preventDefault(); depth.current += 1; setOver(true); }}
            onDragOver={e => { if (disabled || !hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
            onDragLeave={e => { if (disabled || !hasFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); }}
            onDrop={e => { if (disabled || !hasFiles(e)) return; e.preventDefault(); depth.current = 0; setOver(false); accept(e.dataTransfer.files); }}
            className={`relative ${focusable ? 'outline-none rounded-lg focus-visible:ring-2 focus-visible:ring-teal-300' : ''} ${className}`}
            data-testid={testId}
        >
            {children}
            {over && (
                <div className="pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-dashed border-teal-500 bg-teal-50/90 flex items-center justify-center text-[12.5px] font-semibold text-teal-800">
                    <ImagePlus size={16} className="mr-1.5" /> Drop to attach
                </div>
            )}
        </div>
    );
}

// Staged (not yet uploaded) files: image thumbnails so you can see you pasted
// the right screenshot before sending; anything else shows as a pill.
export function StagedFiles({ files, onRemove, testIdPrefix = 'staged-file', className = '' }) {
    const [urls, setUrls] = useState([]);
    useEffect(() => {
        const u = files.map(f => ((f.type || '').startsWith('image/') ? URL.createObjectURL(f) : null));
        setUrls(u);
        return () => u.forEach(x => x && URL.revokeObjectURL(x));
    }, [files]);
    if (!files.length) return null;
    return (
        <div className={`flex flex-wrap gap-2 ${className}`}>
            {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative" data-testid={`${testIdPrefix}-${i}`}>
                    {urls[i] ? (
                        <img src={urls[i]} alt={f.name} title={f.name} className="h-16 w-16 object-cover rounded-md border border-slate-200 bg-white" />
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-full pl-2 pr-5 py-1 text-slate-600"><Paperclip size={10} />{f.name}</span>
                    )}
                    <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f.name}`}
                            className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full p-0.5 text-slate-500 hover:text-rose-600 shadow-sm"
                            data-testid={`${testIdPrefix}-remove-${i}`}>
                        <X size={10} />
                    </button>
                </div>
            ))}
        </div>
    );
}
